'use client'

import shipperCategoryService from '@/_service/shipper-category.service'
import type { ShipperCategoryResponse } from '@/_service/shipper-category.service'
import {
  getShipperCategorySlug,
  matchesShipperCategory,
} from '@/_lib/filter-slugs'
import { Box, Container, Skeleton, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryState } from 'nuqs'
import { useMemo } from 'react'
import CategoryFilters from './components/CategoryFilters'
import CategoryFiltersMobile from './components/CategoryFiltersMobile'

const MAX_VISIBLE_CATEGORIES = 7

interface CategoriesListProps {
  shipperId?: string | null
  initialData?: ShipperCategoryResponse | null
}

const CategoriesList = ({ shipperId, initialData }: CategoriesListProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const [category, setCategory] = useQueryState('category')

  const activeCategory = category ?? 'all'

  const { data, isLoading } = useQuery({
    queryKey: ['shipper-categories-list', shipperId],
    queryFn: () =>
      shipperCategoryService
        .getAll({ page: 1, limit: 30 }, shipperId ?? undefined)
        .then((res) => res.data),
    initialData: initialData ?? undefined,
  })

  const categories = useMemo(() => data?.shipper_categories ?? [], [data])

  const visibleCategories = useMemo(
    () => categories.slice(0, MAX_VISIBLE_CATEGORIES),
    [categories],
  )
  const hiddenCategories = useMemo(
    () => categories.slice(MAX_VISIBLE_CATEGORIES),
    [categories],
  )
  const hiddenActive = hiddenCategories.find((cat) =>
    matchesShipperCategory(activeCategory, cat),
  )

  const updateCategory = (categoryIdOrSlug: string) => {
    if (categoryIdOrSlug === 'all') {
      setCategory(null)
      return
    }
    const match = categories.find(
      (cat) =>
        cat.id === categoryIdOrSlug ||
        getShipperCategorySlug(cat) === categoryIdOrSlug,
    )
    setCategory(match ? getShipperCategorySlug(match) : categoryIdOrSlug)
  }

  if (isLoading && !categories.length) {
    return (
      <Container px={{ base: 4, lg: 14 }} mt={8}>
        <Skeleton height='24px' width='200px' mb={4} />
        <Skeleton height='56px' borderRadius='24px' />
      </Container>
    )
  }

  if (!categories.length) {
    return null
  }

  return (
    <Box py={{ base: 3, lg: 4 }} px={{ base: 4, lg: 14 }}>
      <Text
        fontSize={{ base: 'xl', lg: '2xl' }}
        fontWeight={700}
        mb={{ base: 3, lg: 4 }}
      >
        {t('what_to_order')}
      </Text>
      <Box display={{ base: 'none', lg: 'block' }}>
        <CategoryFilters
          activeCategory={activeCategory}
          visibleCategories={visibleCategories}
          hiddenCategories={hiddenCategories}
          hiddenActive={hiddenActive}
          locale={locale}
          onSelect={updateCategory}
          allLabel={t('all_restaurants')}
          moreLabel={t('more_categories')}
        />
      </Box>
      <Box display={{ base: 'block', lg: 'none' }}>
        <CategoryFiltersMobile categories={categories} />
      </Box>
    </Box>
  )
}

export default CategoriesList
